/**
 * KPIs e Gráfico Evolutivo de Agendamento Online
 * 
 * Exibe cards totalizadores e gráfico evolutivo (diário/mensal)
 * com filtros de período e tipo de evento.
 * 
 * RELEASE: 2026-02-15
 * VERSION: 1.0.0
 */

import { useState } from 'react';
import {
  CalendarCheck,
  CalendarClock,
  CalendarX2,
  Clock,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { Skeleton } from '@/components/ui/skeleton';
import { CrmPeriodFilter } from '@/components/crm/filters/CrmPeriodFilter';
import { SchedulingEventTypeFilter } from './SchedulingEventTypeFilter';
import { useOnlineSchedulingStats } from '@/hooks/useOnlineSchedulingStats';
import { useOnlineSchedulingChart } from '@/hooks/useOnlineSchedulingChart';
import { calculatePeriodDates } from '@/hooks/useCrmOverviewFilters';
import type { PeriodFilter, PeriodPreset } from '@/hooks/useCrmOverviewFilters';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { cn } from '@/lib/utils';

/**
 * Componente principal de KPIs de Agendamento Online
 */
export function OnlineSchedulingKpis() {
  // Estado local de filtros
  const [period, setPeriod] = useState<PeriodFilter>(() => {
    const { startDate, endDate } = calculatePeriodDates('month');
    return { preset: 'month' as PeriodPreset, startDate, endDate };
  });
  const [eventTypeId, setEventTypeId] = useState<string | null>(null);
  // Dados
  const { data: stats, isLoading: statsLoading } = useOnlineSchedulingStats({
    period,
    eventTypeId,
  });

  /** Hook do gráfico diário */
  const { data: dailyData = [], isLoading: dailyLoading } = useOnlineSchedulingChart({
    granularity: 'daily',
    period,
    eventTypeId,
  });

  /** Hook do gráfico mensal */
  const { data: monthlyData = [], isLoading: monthlyLoading } = useOnlineSchedulingChart({
    granularity: 'monthly',
    period,
    eventTypeId,
  });

  // Handlers de filtro de período
  const handlePresetChange = (preset: PeriodPreset) => {
    const { startDate, endDate } = calculatePeriodDates(preset);
    setPeriod({ preset, startDate, endDate });
  };

  const handleCustomChange = (startDate: Date, endDate: Date) => {
    setPeriod({ preset: 'custom', startDate, endDate });
  };

  // Configuração dos cards KPI
  /** Configuração dos cards KPI com cores no padrão Tarefas */
  const kpiCards = [
    {
      label: 'Total Mês',
      value: stats?.total_period ?? 0,
      icon: CalendarCheck,
      iconColor: 'text-purple-700 dark:text-purple-300',
      cardBg: 'bg-purple-100 dark:bg-purple-900/30',
    },
    {
      label: 'Hoje',
      value: stats?.total_today ?? 0,
      icon: Clock,
      iconColor: 'text-cyan-700 dark:text-cyan-300',
      cardBg: 'bg-cyan-100 dark:bg-cyan-900/30',
    },
    {
      label: 'Aberto',
      value: stats?.open_count ?? 0,
      icon: CalendarClock,
      iconColor: 'text-amber-700 dark:text-amber-300',
      cardBg: 'bg-amber-100 dark:bg-amber-900/30',
    },
    {
      label: 'Atrasado',
      value: stats?.overdue_count ?? 0,
      icon: AlertCircle,
      iconColor: 'text-orange-700 dark:text-orange-300',
      cardBg: 'bg-orange-100 dark:bg-orange-900/30',
    },
    {
      label: 'Concluído',
      value: stats?.completed_count ?? 0,
      icon: CheckCircle2,
      iconColor: 'text-green-700 dark:text-green-300',
      cardBg: 'bg-lime-100 dark:bg-green-900/30',
    },
    {
      label: 'Cancelados',
      value: stats?.cancelled_count ?? 0,
      icon: CalendarX2,
      iconColor: 'text-pink-700 dark:text-pink-300',
      cardBg: 'bg-pink-100 dark:bg-pink-900/30',
    },
  ];

  return (
    <div className="space-y-4">
      {/* Barra de filtros */}
      <div className="flex items-center gap-2 flex-wrap">
        <CrmPeriodFilter
          value={period}
          onPresetChange={handlePresetChange}
          onCustomChange={handleCustomChange}
          className="h-[22px] text-xs px-2"
        />
        <SchedulingEventTypeFilter
          value={eventTypeId}
          onChange={setEventTypeId}
          className="h-[22px] text-xs px-2"
        />
      </div>

      {/* Cards KPI - Layout horizontal padrão Tarefas */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpiCards.map((kpi) => (
          <Card
            key={kpi.label}
            className={cn('border-0 shadow-none', kpi.cardBg)}
          >
            <CardContent className="p-3">
              <div className="flex items-center justify-between gap-2">
                {/* Ícone + título */}
                <div className="flex items-center gap-2 min-w-0">
                  <kpi.icon className={cn('h-5 w-5 shrink-0', kpi.iconColor)} />
                  <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100">
                    {kpi.label}
                  </p>
                </div>
                {/* Valor */}
                {statsLoading ? (
                  <div className="h-6 w-8 bg-white/40 dark:bg-white/10 animate-pulse rounded" />
                ) : (
                  <p className="text-xl font-bold leading-tight shrink-0 text-gray-900 dark:text-gray-100">
                    {kpi.value}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Gráficos Evolutivos - Diário e Mensal lado a lado */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Gráfico Evolutivo Diário */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Evolutivo Diário</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {dailyLoading ? (
              <Skeleton className="h-[200px] w-full" />
            ) : dailyData.length === 0 ? (
              <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">
                Nenhum dado no período selecionado
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={dailyData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <XAxis dataKey="period_label" tick={{ fontSize: 11 }} className="text-muted-foreground" axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} className="text-muted-foreground" axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid hsl(var(--border))', backgroundColor: 'hsl(var(--card))', color: 'hsl(var(--foreground))', fontSize: '12px' }} />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  <Area type="monotone" dataKey="confirmed_count" name="Confirmados" stroke="hsl(142, 71%, 45%)" fill="hsl(142, 71%, 45%)" fillOpacity={0.15} strokeWidth={2} />
                  <Area type="monotone" dataKey="cancelled_count" name="Cancelados" stroke="hsl(0, 70%, 65%)" fill="hsl(0, 70%, 65%)" fillOpacity={0.1} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Gráfico Evolutivo Mensal */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Evolutivo Mensal</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {monthlyLoading ? (
              <Skeleton className="h-[200px] w-full" />
            ) : monthlyData.length === 0 ? (
              <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">
                Nenhum dado no período selecionado
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={monthlyData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <XAxis dataKey="period_label" tick={{ fontSize: 11 }} className="text-muted-foreground" axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} className="text-muted-foreground" axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid hsl(var(--border))', backgroundColor: 'hsl(var(--card))', color: 'hsl(var(--foreground))', fontSize: '12px' }} />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  <Bar dataKey="confirmed_count" name="Confirmados" fill="hsl(142, 71%, 45%)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="cancelled_count" name="Cancelados" fill="hsl(0, 70%, 65%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
